import { useEffect, useId, useRef, useState, forwardRef } from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";
import { comnUtils } from "../../utils";
import { BOLD_TEXT, COLOR_BORDER, COLOR_TEXT, SIZE_TEXT } from "../../features/foundation";

/** */
export type InputTextareaProps = {
    edit?: boolean;
    letterCase?: "upper" | "lower";
    mode?: "edit" | "view" | null;

    name?: string;
    value?: any;
    rows?: number;
    readOnly?: boolean;
    disabled?: boolean;
    maxLength?: number;
    placeholder?: string;
    defaultValue?: any;

    color?: keyof typeof COLOR_TEXT;
    editColor?: keyof typeof COLOR_TEXT;
    borderColor?: keyof typeof COLOR_BORDER;
    bold?: keyof typeof BOLD_TEXT;
    editBold?: keyof typeof BOLD_TEXT;
    fontSize?: keyof typeof SIZE_TEXT;

    onBlur?: (arg?: any) => void;
    onFocus?: (arg?: any) => void;
    onChange?: (arg?: any) => void;

    options?: any[];
    openOptions?: (...arg: any) => any;
    onSelectOption?: (...arg: any) => any;
    disableOptionsFilter?: boolean;
};

export const Textarea = forwardRef<HTMLTextAreaElement, InputTextareaProps>(
    (props: InputTextareaProps, ref: React.ForwardedRef<HTMLTextAreaElement>) => {
        const {
            edit = true,
            mode,
            letterCase,
            /** input props */
            name,
            value,
            onChange,
            onBlur,
            onFocus,

            readOnly,
            disabled,
            maxLength,
            placeholder,

            color,
            editColor,
            borderColor,
            bold,
            editBold,
            fontSize,

            rows,

            options,
            disableOptionsFilter,
            onSelectOption,
            openOptions,
        } = props;

        const _props = Object.fromEntries(
            Object.entries({
                name,
                readOnly,
                disabled,
                maxLength,
                placeholder,
                rows,
                onBlur,
                onFocus,
            }).filter(([, value]) => value !== undefined),
        );

        const _context = useRef<any>({ container: null });
        const [autoCompleteOpen, setAutoCompleteOpen] = useState(false);

        const o = { letterCase };

        const handleClick = () => {
            /* ac */
            if (Array.isArray(options)) {
                if (options.length) {
                    setAutoCompleteOpen(true);
                }
            }
        };

        const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            if (maxLength !== undefined && e.target.value.length > maxLength) {
                return;
            }

            /* ac */
            if (Array.isArray(options)) {
                //@ts-ignore
                if (options.length && e.nativeEvent?.data !== null) {
                    setAutoCompleteOpen(true);
                }
            }

            if (onChange) {
                onChange(formatTextarea(e.target.value, o));
            }
        };

        const handleSelectOption = onSelectOption
            ? (optionValue: any) => {
                  if (onChange) {
                      if (onSelectOption) {
                          onChange(onSelectOption(value, optionValue));
                      }
                  }
              }
            : null;

        const EDIT_STATUS = mode === "edit" ? true : mode === "view" ? false : edit;

        let autoComplete: any;
        if (value && Array.isArray(options)) {
            if (options.length) {
                autoComplete = !disableOptionsFilter
                    ? options.filter((_) => {
                          return _?.toUpperCase().includes(value.toUpperCase());
                      })
                    : options;
            }
        }

        const acoc = openOptions instanceof Function ? Boolean(openOptions(value)) : true;

        return (
            <div
                ref={(element) => {
                    if (!element) return;
                    _context.current.container = element;
                }}
                className="w-full"
            >
                {!EDIT_STATUS && (
                    <pre
                        title={value}
                        className={classNames(
                            "w-fit whitespace-pre-wrap break-all",
                            comnUtils.getViewStyle(color, bold, fontSize),
                        )}
                    >
                        {value}
                    </pre>
                )}
                <div hidden={!EDIT_STATUS}>
                    <textarea
                        {..._props}
                        ref={ref}
                        value={value || ""}
                        title={value}
                        onChange={handleChange}
                        onClick={handleClick}
                        className={classNames(
                            "textarea" + comnUtils.getEditStyle(editColor, editBold),
                            o?.letterCase === "upper" && "uppercase",
                            o?.letterCase === "lower" && "lowercase",
                            borderColor ? COLOR_BORDER[borderColor] : "",
                        )}
                    />
                    {!!autoComplete?.length && autoCompleteOpen && acoc && (
                        <Auto
                            inputTextContext={_context}
                            autoComplete={autoComplete}
                            setAutoCompleteOpen={setAutoCompleteOpen}
                            onChange={onChange}
                            handleSelectOption={handleSelectOption}
                        />
                    )}
                </div>
            </div>
        );
    },
);

const Auto = (props?: any) => {
    const { inputTextContext, autoComplete, onChange, setAutoCompleteOpen, handleSelectOption } = props;
    const containerRect = inputTextContext.current.container.getBoundingClientRect();

    const id = useId();
    const _context = useRef<any>({ autoComplete: [], selected: null });
    const [_selected, _setSelected] = useState<any>(null);

    const handleClose = () => {
        setAutoCompleteOpen(false);
    };

    useEffect(() => {
        _context.current.autoComplete = autoComplete;
    }, [autoComplete]);

    useEffect(() => {
        const handleKeydown = (event: any) => {
            switch (event.keyCode) {
                case 27: // esc
                case 9: // tab
                    handleClose();
                    break;
                case 38: // up
                    _setSelected((prev: any) => {
                        const val = prev === null ? 0 : prev - 1;
                        const next = val < 0 ? prev : val;
                        _context.current.selected = next;
                        return next;
                    });
                    break;
                case 40: // down
                    _setSelected((prev: any) => {
                        const val = prev === null ? 0 : prev + 1;
                        const next = val > _context.current.autoComplete.length - 1 ? prev : val;
                        _context.current.selected = next;
                        return next;
                    });
                    break;
                case 13: // enter
                    if (typeof _context.current.selected !== "number") return;
                    const value = _context.current.autoComplete[_context.current.selected];
                    if (onChange) {
                        if (handleSelectOption) {
                            handleSelectOption(value);
                        } else {
                            onChange(value);
                        }
                    }
                    setAutoCompleteOpen(false);
                    break;
            }
        };

        window.addEventListener("keydown", handleKeydown);
        window.addEventListener("wheel", handleClose);
        window.addEventListener("scroll", handleClose);
        window.addEventListener("resize", handleClose);
        return () => {
            window.removeEventListener("keydown", handleKeydown);
            window.removeEventListener("wheel", handleClose);
            window.removeEventListener("scroll", handleClose);
            window.addEventListener("resize", handleClose);
        };
    }, []);

    return createPortal(
        <div className="fixed top-0 left-0 w-screen h-screen z-[9998]" onClick={handleClose}>
            <ul
                className="fixed rounded bg-card min-w-[12rem] border divide-y shadow z-[9999]"
                style={{
                    top: containerRect.top + containerRect.height + 4,
                    left: containerRect.left,
                    width: containerRect.width,
                }}
            >
                {autoComplete.map((item: any, i: any) => {
                    return (
                        <li
                            key={id + "." + i}
                            aria-selected={_selected === i}
                            className="flex px-1.5 items-center h-[2rem] cursor-pointer hover:bg-lightgray aria-selected:bg-lightgray"
                            onClick={() => {
                                if (onChange) {
                                    if (handleSelectOption) {
                                        handleSelectOption(item);
                                    } else {
                                        onChange(item);
                                    }
                                }
                                setAutoCompleteOpen(false);
                            }}
                        >
                            {item}
                        </li>
                    );
                })}
            </ul>
        </div>,
        document.body,
    );
};

export const formatTextarea = (v: any, o?: any) => {
    if (!v) return "";
    let f = String(v);

    if (o?.letterCase === "upper") {
        f = f.toUpperCase();
    }
    if (o?.letterCase === "lower") {
        f = f.toLowerCase();
    }

    return f;
};

export const unformatTextarea = (v: any, o?: any) => {
    if (v) return undefined;
    return String(v);
};
